import React from "react";
import { commitMutation, graphql } from "react-relay";

import { environment } from "~/models";
import ApiKeyGeneratedModal from "~/settings/SettingsModal/ApiKeyGeneratedModal";
import CreateApiKeyModal from "~/settings/SettingsModal/CreateApiKeyModal";
import { __globalModalContext } from "~/ui/modals";
import toast from "~/ui/toast";

import type { hooksCreateApiKeyPrivilegedMutation } from "~/__generated__/hooksCreateApiKeyPrivilegedMutation.graphql";
import type { hooksCreateApiKeyStandardMutation } from "~/__generated__/hooksCreateApiKeyStandardMutation.graphql";

const workspaceApiKeyMutation = graphql`
	mutation hooksCreateApiKeyPrivilegedMutation($name: String!) {
		createPrivilegedApiKey(name: $name) {
			token
		}
	}
`;

const userApiKeyMutation = graphql`
	mutation hooksCreateApiKeyStandardMutation($name: String!) {
		createStandardApiKey(name: $name) {
			token
		}
	}
`;

export const useCreateApiKey = (privileged = false, refetch?: () => void) => {
	const openApiKeyGeneratedModal = React.useCallback((name: string, token: string) => {
		__globalModalContext?.open(({ close }) => <ApiKeyGeneratedModal token={token} name={name} onClose={close} />);
	}, []);

	const handleError = React.useCallback((_error: Error) => {
		toast.error("Failed to create API key");
	}, []);

	const runCreatePrivilegedKeyMutation = React.useCallback(
		(name: string) => {
			const handleCompleted = (response: hooksCreateApiKeyPrivilegedMutation["response"]) => {
				const result = response.createPrivilegedApiKey;
				if (result?.token) {
					const token = result.token;
					openApiKeyGeneratedModal(name, token);
					refetch?.();
				}
			};

			commitMutation<hooksCreateApiKeyPrivilegedMutation>(environment, {
				mutation: workspaceApiKeyMutation,
				variables: { name },
				onCompleted: handleCompleted,
				onError: handleError,
			});
		},
		[handleError, openApiKeyGeneratedModal, refetch],
	);

	const runCreateStandardKeyMutation = React.useCallback(
		(name: string) => {
			const handleCompleted = (response: hooksCreateApiKeyStandardMutation["response"]) => {
				const result = response.createStandardApiKey;
				if (result?.token) {
					const token = result.token;
					openApiKeyGeneratedModal(name, token);
					refetch?.();
				}
			};

			commitMutation<hooksCreateApiKeyStandardMutation>(environment, {
				mutation: userApiKeyMutation,
				variables: { name },
				onCompleted: handleCompleted,
				onError: handleError,
			});
		},
		[handleError, openApiKeyGeneratedModal, refetch],
	);

	const createKey = React.useCallback(() => {
		if (!__globalModalContext) return;

		const onSubmit = privileged ? runCreatePrivilegedKeyMutation : runCreateStandardKeyMutation;

		__globalModalContext.open<string | null>(({ close }) => (
			<CreateApiKeyModal onClose={close} onSubmit={onSubmit} privileged={privileged} />
		));
	}, [privileged, runCreatePrivilegedKeyMutation, runCreateStandardKeyMutation]);

	return {
		createKey,
	};
};
